import { z } from 'zod';
import { stripHtmlTags } from '@/lib/helpers';
import { decodeHtmlEntities } from '../logic/templateVariables.logic';
import { DelayUnit } from '../emails/types';

/**
 * Helper function to strip HTML tags and decode HTML entities
 * Uses shared utilities for consistency across the application
 */
export const stripHtml = (html: string): string => {
    let text = stripHtmlTags(html);
    text = decodeHtmlEntities(text);
    return text.trim();
};

/**
 * Custom validation for HTML content (from TipTap editor)
 * Ensures the content is not empty after stripping HTML tags and trimming whitespace
 */
export const htmlContentValidator = (value: string) => {
    const strippedContent = stripHtml(value).trim();
    return strippedContent.length > 0;
};

/**
 * Shared email subject validation
 * - Required field
 * - Cannot be empty after stripping HTML
 * - Maximum 200 characters (after stripping HTML)
 */
export const emailSubjectValidation = z
    .string()
    .min(1, 'Email subject cannot be empty')
    .refine(val => val.trim().length > 0, {
        message: 'Email subject cannot be empty',
    })
    .refine(val => htmlContentValidator(val), {
        message: 'Email subject cannot be empty',
    })
    .refine(
        val => {
            const strippedContent = stripHtml(val);
            return strippedContent.length <= 200;
        },
        {
            message: 'Email subject must be 200 characters or less',
        }
    );

/**
 * Shared email body validation
 * - Required field
 * - Cannot be empty after stripping HTML
 */
export const emailBodyValidation = z
    .string()
    .min(1, 'Email body is required')
    .refine(val => val.trim().length > 0, {
        message: 'Email body cannot be empty',
    })
    .refine(val => htmlContentValidator(val), {
        message: 'Email body cannot be empty',
    });

/**
 * Shared delay validation for email timing (for drafts - allows empty)
 * - Must be a number
 * - Must be a whole number
 * - Allows empty/0 for drafts and first time-based emails
 * Note: Max value validation is handled at the form level with delayUnit context
 */
export const emailDelayValidation = z
    .string()
    .refine(
        val => {
            // Allow empty/undefined for drafts and first time-based emails
            if (!val || val === '' || val === '0') return true;
            return !isNaN(Number(val));
        },
        {
            message: 'Delay must be a number',
        }
    )
    .refine(
        val => {
            if (!val || val === '' || val === '0') return true;
            return Number.isInteger(Number(val));
        },
        {
            message: 'Delay must be a whole number',
        }
    );

/**
 * Strict delay validation for publishing (requires a value)
 * - Must be a whole number >= 1
 * - Cannot be empty (except for first time-based emails)
 * Note: Max value validation is handled at the form level with delayUnit context
 */
export const emailDelayValidationStrict = z
    .string()
    .min(1, 'Delay is required')
    .refine(
        val => {
            const num = Number(val);
            return !isNaN(num) && Number.isInteger(num) && num >= 1;
        },
        {
            message: 'Delay must be a whole number of at least 1',
        }
    );

/**
 * Shared delay unit validation
 */
export const emailDelayUnitValidation = z.enum(['minutes', 'hours', 'days']);

export const MAX_MINUTES = 60;
export const MAX_HOURS = 24;
export const MAX_DAYS = 365;

/**
 * Get maximum delay value based on unit
 * - Minutes: 60 (1 hour max)
 * - Hours: 24 (1 day max)
 * - Days: 365 (1 year max)
 */
export const getMaxDelayForUnit = (unit: DelayUnit): number => {
    switch (unit) {
        case 'minutes':
            return MAX_MINUTES;
        case 'hours':
            return MAX_HOURS;
        case 'days':
            return MAX_DAYS;
        default:
            return 1;
    }
};

/**
 * Get user-friendly unit name for error messages
 */
export const getDelayUnitLabel = (unit: DelayUnit): string => {
    switch (unit) {
        case 'minutes':
            return 'minute';
        case 'hours':
            return 'hour';
        case 'days':
            return 'day';
        default:
            return 'day';
    }
};

/**
 * Validates that delay is between 1 and the maximum allowed for the selected unit
 */
export const validateDelayRange = (data: { delay: string | number; delayUnit: DelayUnit }) => {
    const maxDelay = getMaxDelayForUnit(data.delayUnit);
    const delayValue = Number(data.delay);

    return delayValue >= 1 && delayValue <= maxDelay;
};

export const getDelayRangeErrorMessage = (data: { delayUnit: DelayUnit }) => {
    const maxDelay = getMaxDelayForUnit(data.delayUnit);
    const unitLabel = getDelayUnitLabel(data.delayUnit);

    return {
        message: `Delay must be between 1 and ${maxDelay} ${unitLabel}${maxDelay > 1 ? 's' : ''}`,
        path: ['delay'],
    };
};
